import { isEmpty, isNil } from 'lodash';

import env from '@/env';
import { authHelpers } from '@/services/auth';
import { serviceDirectoryApiClient } from '@/services/service-directory';

export interface EntityForm {
  organisationId: string;
  entityType?: string;
  name: string;
  logo?: File;
  primaryColor: string;
  accentColor: string;
  legalName?: string;
  description?: string;
  contact?: string;
  landingPageTitleLine1?: string;
  landingPageTitleLine2?: string;
  landingPageTitleLine3?: string;
  landingPageWelcomeHeaderText?: string;
  landingPageWelcomeBodyText?: string;
  landingPageAppIntroHeaderText?: string;
  landingPageAppIntroBodyText?: string;
  landingPageApiIntroHeaderText?: string;
  landingPageApiIntroBodyText?: string;
  landingPageApiIntroPendingText?: string;
  landingPageNewsIntroText?: string;
  showApiOverview?: boolean;
  locRegion?: string;
  locCountry?: string;
  locCountryCode?: string;
  isOpenBankingCompliance?: boolean;
  line1?: string;
  line2?: string;
  line3?: string;
  buildingName?: string;
  buildingNumber?: string;
  apartmentName?: string;
  city?: string;
  postcode?: string;
  country?: string;
  latitude?: string;
  longitude?: string;
  apiPortalLink?: string;
  webSiteLink?: string;
  linkedIn?: string;
  facebook?: string;
  twitter?: string;
  google?: string;
  showProcess?: boolean;
  showTestimonials?: boolean;
  showNews?: boolean;
  showPartnerships?: boolean;
  showShowcase?: boolean;
  showSubscribe?: boolean;
  showApps?: boolean;
  showApis?: boolean;
  showServices?: boolean;
  isActive?: boolean;
  isAvailable?: boolean;
  tags?: string[];
  entityFeatures?: string;
  sponsored?: boolean;
  sponsorRating?: number;
}

class Entity {
  fetchEntities = async (
    pageNumber: number,
    pageSize: number,
    searchEntity: string,
    filterLabels?: { [key: string]: string[] },
    location?: string,
    region?: string,
    country?: string,
    listOrders?: string,
    isAdminAffect?: boolean,
    tag?: string,
    feature?: string,
    captchaToken?: string
  ) => {
    let defaultOrder = 'updatedAt-desc';
    if (
      searchEntity ||
      !isEmpty(filterLabels?.entityType) ||
      !isEmpty(filterLabels?.complianceLevel) ||
      !isEmpty(filterLabels?.apiCategories) ||
      location ||
      region ||
      country ||
      tag ||
      feature
    ) {
      defaultOrder = 'displayName-asc';
    }
    const response = await serviceDirectoryApiClient.get(`entities`, {
      params: {
        pageNumber,
        pageSize,
        listOrders: listOrders || defaultOrder,
        searchText: searchEntity,
        entityType: filterLabels?.entityType?.join(','),
        complianceLevel: filterLabels?.complianceLevel?.join(','),
        apiCategories: filterLabels?.apiCategories?.join(','),
        // appFeatures: filterLabels?.appFeatures?.join(','),
        location,
        region,
        country,
        isAdminAffect,
        tag,
        feature,
      },
      headers: {
        recaptchatoken: captchaToken,
      },
    });
    return response.data;
  };

  fetchEntitiesDirectory = async (
    pageNumber: number,
    pageSize: number,
    searchEntity: string,
    dataSource?: string,
    isAdminAffect?: boolean
  ) => {
    const response = await serviceDirectoryApiClient.get(`entities`, {
      params: {
        pageNumber,
        pageSize,
        listOrders: 'updatedAt-desc',
        searchText: searchEntity.trim(),
        dataSource,
        isAdminAffect,
      },
    });
    return response.data;
  };

  fetchEntityBySubdomain = async (subdomain: string, isAdminAffect?: boolean, recaptchaToken?: string) => {
    const response = await serviceDirectoryApiClient.get(`entities/subdomain/${subdomain}`, {
      params: {
        isAdminAffect,
      },
      headers: {
        recaptchatoken: recaptchaToken,
      },
    });
    return response.data;
  };

  fetchEntityById = async (entityId: string) => {
    const response = await serviceDirectoryApiClient.get(`entities/${entityId}`);
    return response.data;
  };

  fetchEntitiesComparison = async (
    pageNumber?: number,
    pageSize?: number,
    complianceGroups?: string,
    entityName?: string,
    countries?: string,
    sort?: string
  ) => {
    const response = await serviceDirectoryApiClient.get(`entities/comparison`, {
      params: {
        pageNumber,
        pageSize,
        complianceGroups,
        entityName,
        countries,
        sort,
      },
    });
    return response.data;
  };

  createOrUpdateEntity = async (entity: EntityForm, adding?: boolean) => {
    const formData = this.formDataFromEntity(entity, env.siteId);
    const endpoint = 'entities';
    const headers = {
      'Content-Type': 'multipart/form-data',
      organisationId: entity.organisationId,
    };
    const response = adding
      ? await serviceDirectoryApiClient.post(endpoint, formData, { headers })
      : await serviceDirectoryApiClient.put(endpoint, formData, { headers });

    return response.data;
  };

  deleteEntity = async (entityId: string) => {
    const response = await serviceDirectoryApiClient.delete(`entities/${entityId}`);
    return response.data;
  };

  getDefaultApis = async (organisationId: string) => {
    const accessToken = authHelpers.getAccessToken();
    const body = { accessToken, organisationId };
    const response = await serviceDirectoryApiClient.post('entities/defaults', body);
    return response.data;
  };

  reloadDefaultEntity = async (organisationId: string, type: string) => {
    const accessToken = authHelpers.getAccessToken();
    const body = { accessToken, type };
    const response = await serviceDirectoryApiClient.post('entities/defaults/reload', body, {
      headers: {
        'Content-Type': 'application/json',
        organisationId,
      },
    });
    return response.data;
  };

  fetchCountries = async () => {
    const response = await serviceDirectoryApiClient.get(`entities/countries`);
    return response.data;
  };

  generateIndexes = async () => {
    const response = await serviceDirectoryApiClient.post('indexes');
    return response.data;
  };

  private formDataFromEntity = (entity: EntityForm, appId: string) => {
    const formData = new FormData();
    formData.append('appId', appId);
    formData.append('name', entity.name);
    formData.append('entityType', entity.entityType || 'Bank');
    entity?.logo && formData.append('logo', entity.logo);
    entity?.primaryColor && formData.append('primaryColor', entity.primaryColor ?? '');
    entity?.accentColor && formData.append('accentColor', entity.accentColor ?? '');
    formData.append('legalName', entity.legalName ?? '');
    formData.append('description', entity.description ?? '');
    formData.append('contact', entity.contact ?? '');
    formData.append('locRegion', entity.locRegion ?? '');
    formData.append('locCountry', entity.locCountry ?? '');
    formData.append('locCountryCode', entity.locCountryCode ?? '');
    formData.append('linkedIn', entity.linkedIn ?? '');
    formData.append('apiPortalLink', entity.apiPortalLink ?? '');
    formData.append('webSiteLink', entity.webSiteLink ?? '');
    formData.append('google', entity.google ?? '');
    formData.append('twitter', entity.twitter ?? '');
    formData.append('facebook', entity.facebook ?? '');
    formData.append('tags', entity.tags ? entity.tags.join(',') : '');
    !isNil(entity?.isActive) && formData.append('isActive', entity?.isActive?.toString());
    !isNil(entity?.isAvailable) && formData.append('isAvailable', entity?.isAvailable?.toString());
    entity?.landingPageTitleLine1 && formData.append('landingPageTitleLine1', entity?.landingPageTitleLine1 ?? '');
    entity?.landingPageTitleLine2 && formData.append('landingPageTitleLine2', entity?.landingPageTitleLine2 ?? '');
    entity?.landingPageTitleLine3 && formData.append('landingPageTitleLine3', entity?.landingPageTitleLine3 ?? '');
    entity?.landingPageWelcomeHeaderText &&
      formData.append('landingPageWelcomeHeaderText', entity?.landingPageWelcomeHeaderText ?? '');
    entity?.landingPageWelcomeBodyText &&
      formData.append('landingPageWelcomeBodyText', entity?.landingPageWelcomeBodyText ?? '');
    entity?.landingPageAppIntroHeaderText &&
      formData.append('landingPageAppIntroHeaderText', entity?.landingPageAppIntroHeaderText ?? '');
    entity?.landingPageAppIntroBodyText &&
      formData.append('landingPageAppIntroBodyText', entity?.landingPageAppIntroBodyText ?? '');
    entity?.landingPageApiIntroHeaderText &&
      formData.append('landingPageApiIntroHeaderText', entity?.landingPageApiIntroHeaderText ?? '');
    entity?.landingPageApiIntroBodyText &&
      formData.append('landingPageApiIntroBodyText', entity?.landingPageApiIntroBodyText ?? '');
    entity?.landingPageApiIntroPendingText &&
      formData.append('landingPageApiIntroPendingText', entity?.landingPageApiIntroPendingText ?? '');
    entity?.landingPageNewsIntroText &&
      formData.append('landingPageNewsIntroText', entity?.landingPageNewsIntroText ?? '');
    !isNil(entity.isOpenBankingCompliance) &&
      formData.append('isOpenBankingCompliance', entity.isOpenBankingCompliance.toString());

    // Address Data
    entity?.line1 && formData.append('line1', entity.line1.toString());
    entity?.line2 && formData.append('line2', entity.line2.toString());
    entity?.line3 && formData.append('line3', entity.line3.toString());
    entity?.buildingName && formData.append('buildingName', entity.buildingName.toString());
    entity?.buildingNumber && formData.append('buildingNumber', entity.buildingNumber.toString());
    entity?.apartmentName && formData.append('apartmentName', entity.apartmentName.toString());
    entity?.city && formData.append('city', entity.city.toString());
    entity?.postcode && formData.append('postcode', entity.postcode.toString());
    entity?.country && formData.append('country', entity.country.toString());
    entity?.latitude && formData.append('latitude', entity.latitude.toString());
    entity?.longitude && formData.append('longitude', entity.longitude.toString());

    // Sections Flag
    !isNil(entity.showProcess) && formData.append('showProcess', entity.showProcess.toString());
    !isNil(entity.showTestimonials) && formData.append('showTestimonials', entity.showTestimonials.toString());
    !isNil(entity.showNews) && formData.append('showNews', entity.showNews.toString());
    !isNil(entity.showPartnerships) && formData.append('showPartnerships', entity.showPartnerships.toString());
    !isNil(entity.showShowcase) && formData.append('showShowcase', entity.showShowcase.toString());
    !isNil(entity.showSubscribe) && formData.append('showSubscribe', entity.showSubscribe.toString());
    !isNil(entity.showApiOverview) && formData.append('showApiOverview', entity.showApiOverview.toString());
    !isNil(entity.showApis) && formData.append('showApis', entity.showApis.toString());
    !isNil(entity.showApps) && formData.append('showApps', entity.showApps.toString());
    !isNil(entity.showServices) && formData.append('showServices', entity.showServices.toString());
    !isNil(entity.sponsored) && formData.append('sponsored', entity.sponsored.toString());
    !isNil(entity?.sponsorRating) && formData.append('sponsorRating', entity.sponsorRating.toString());

    // Entity Features
    formData.append('entityFeatures', entity?.entityFeatures ?? '');

    return formData;
  };

  fetchRelatedEntity = async (
    currentEntityId?: string,
    total?: number,
    country?: string,
    region?: string,
    entityType?: string
  ) => {
    const response = await serviceDirectoryApiClient.get(`entities/related`, {
      params: {
        currentEntityId,
        total,
        country,
        region,
        entityType,
      },
    });
    return response.data;
  };
}

export const EntityInstance = new Entity();
